import { observable } from 'mobx';
import type { Moment } from '@this/lib/moment';
import moment from '@this/lib/moment';

const travelAuthorizationServices = {
  visa: 'ビザ',
  esta: 'ESTA'
};

export type TravelAuthorizationServiceType = keyof typeof travelAuthorizationServices;

export type TravelAuthorizationElementResponseArgs = {
  id: number;
  order_item_id?: number;
  service?: TravelAuthorizationServiceType;
  service_text: string;
  content?: string;
  started_at?: string;
  ended_at?: string;
};

export type TravelAuthorizationElementArgs = {
  id: number;
  orderItemId?: number;
  service?: TravelAuthorizationServiceType;
  serviceText: string;
  content?: string;
  startedAt?: Moment;
  endedAt?: Moment;
};

export const convertTravelAuthorizationElementResponseToArgs = (
  response: TravelAuthorizationElementResponseArgs
) => {
  const args: TravelAuthorizationElementArgs = {
    id: response.id,
    orderItemId: response.order_item_id,
    service: response.service,
    serviceText: response.service_text,
    content: response.content,
    startedAt: response.started_at ? moment(response.started_at) : undefined,
    endedAt: response.ended_at ? moment(response.ended_at) : undefined
  };
  return args;
};

export default class TravelAuthorizationElement {
  @observable id: number;
  @observable orderItemId?: number;
  @observable service?: TravelAuthorizationServiceType;
  @observable serviceText: string;
  @observable content?: string;
  @observable startedAt?: Moment;
  @observable endedAt?: Moment;

  constructor(args: TravelAuthorizationElementArgs) {
    this.id = args.id;
    this.orderItemId = args.orderItemId;
    this.service = args.service;
    this.serviceText = args.serviceText;
    this.content = args.content;
    this.startedAt = args.startedAt;
    this.endedAt = args.endedAt;
  }

  get type() {
    return 'travel_authorization';
  }

  get startDate() {
    return null;
  }

  get endDate() {
    return null;
  }

  get title() {
    return '査証・渡航認証関連';
  }

  get summary() {
    return `[査証] ${this.serviceText}`;
  }

  get name() {
    return this.serviceText;
  }

  // TODO: js/src/domain/order_item2.tsでフォールバックしてるから不要
  get mappingDescription(): string {
    return this.title;
  }

  detail() {
    return '';
  }

  validationErrors(_orderItemStatus?: number): string[] {
    const errors: string[] = [];

    if (!this.service) errors.push('査証・渡航認証関連のサービスを入力してください');
    if (!this.content) errors.push('査証・渡航認証関連の内容を入力してください');
    if (this.endedAt && this.startedAt && this.endedAt < this.startedAt)
      errors.push('終了日は開始日以降を指定してください');

    return errors;
  }

  get updateParams() {
    return {
      type: this.type,
      order_item_id: this.orderItemId,
      service: this.service,
      content: this.content,
      started_at: this.startedAt?.format('YYYY-MM-DD HH:mm:ss'),
      ended_at: this.endedAt?.format('YYYY-MM-DD HH:mm:ss')
    };
  }

  get description(): string {
    return `<${this.title}>
    ■サービス：${this.service && travelAuthorizationServices[this.service]}
    ■内容
    ${this.content}
    ■開始日時：${this.startedAt?.format('YYYY/MM/DD HH:mm:ss')}
    ■終了日時：${this.endedAt?.format('YYYY/MM/DD HH:mm:ss')}\
    `;
  }

  get startTime() {
    return this.startedAt;
  }

  get endTime() {
    return this.endedAt;
  }
}
